/**
 * ResumableEventSource — a fetch+ReadableStream replacement for the
 * native EventSource that supports custom request headers (so we can
 * send Last-Event-ID on manual reconnect to resume from the cursor).
 *
 * The native browser EventSource doesn't expose a way to set request
 * headers — it sends Last-Event-ID automatically on its own retry,
 * but only when the BROWSER decides to reconnect, and only with the
 * id it last received. We want explicit control:
 *   - Reconnect from a cursor stored in localStorage (page reload).
 *   - Reconnect after the user navigates back to a chat whose stream
 *     was interrupted.
 *   - Reconnect after a chaos-injected drop (test harness).
 *
 * Surface mirrors EventSource enough that useSSEListener can swap one
 * for the other without changes:
 *   - new ResumableEventSource(url, { lastEventId, withCredentials })
 *   - readyState (CONNECTING / OPEN / CLOSED)
 *   - onopen, onmessage, onerror
 *   - addEventListener(type, fn) / removeEventListener
 *   - close()
 *   - reconnect({ lastEventId })  — new
 *
 * Parsing follows the SSE format from
 *   https://html.spec.whatwg.org/multipage/server-sent-events.html
 * minus the BOM handling (we don't expect a BOM from our Flask backend).
 */

const CONNECTING = 0;
const OPEN = 1;
const CLOSED = 2;

const ABORT_REASON_CLOSED = "ResumableEventSource:closed";

// Optional chaos integration — `?chaos=1` builds load the controller
// alongside the dev panel. We import lazily so production bundles
// don't drag the chaos module in.
let _getChaosController = null;
const _tryLoadChaos = () => {
  if (_getChaosController !== null) return _getChaosController;
  try {
    // eslint-disable-next-line global-require
    _getChaosController = require("../dev/chaos/ChaosController").getChaosController;
  } catch (_) {
    _getChaosController = false;
  }
  return _getChaosController;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const parseSSEBlock = (block) => {
  let type = "message";
  let data = [];
  let id = null;
  let retry = null;
  let sawField = false; // distinguishes a comments-only block from a real event
  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue; // comment / keepalive
    sawField = true;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") type = value;
    else if (field === "data") data.push(value);
    else if (field === "id") id = value;
    else if (field === "retry") {
      const n = parseInt(value, 10);
      if (!Number.isNaN(n)) retry = n;
    }
  }
  if (!sawField) return null; // pure-comment block — drop, don't emit a phantom event
  return { type, data: data.join("\n"), lastEventId: id, retry };
};

const parseSSEChunk = (text, carry) => {
  // SSE events are separated by a blank line. Returns an array of
  // {type, data, id, retry} dicts plus any tail string left over for
  // the next chunk.
  //
  // PLEX-6483: this used to do `(carry + text).replace(/\r\n?/g, "\n")`
  // then `.split("\n\n")` on the WHOLE accumulated buffer on every call —
  // three full passes over everything received so far, every single
  // chunk. For one very long SSE line with no "\n\n" until the very end
  // (a multi-MB JSON data: field — exactly what a highly-connected
  // compound search result looks like), that's an O(n^2) re-scan: the
  // same already-known-boundary-free bytes get re-normalized and
  // re-split again and again as more chunks arrive. This is the same
  // anti-pattern found in a Python benchmark script using
  // requests.iter_lines() against this same endpoint (14MB+ single-line
  // SSE payloads measured 2s of real server time vs. 90-270s of client
  // "read" time — entirely the client re-scanning its own buffer).
  //
  // Fix: `carry` is guaranteed to contain no "\n\n" (any such boundary
  // would already have been split off and returned as an event by a
  // prior call), so a new boundary can only start at or after
  // `carry.length - 1` (the -1 covers a boundary split across the
  // carry/text join). Normalize only the new `text`, not the whole
  // buffer, and search for boundaries with indexOf from that offset
  // instead of regex+split over everything.
  // A lone trailing \r is ambiguous — it might be the first half of a
  // CRLF split across this chunk and the next, so leave it un-normalized
  // rather than eagerly turning it into \n (plain /\r\n?/g would do that,
  // since the \n is optional in that pattern).
  let normalizedText;
  if (text.endsWith("\r")) {
    normalizedText = `${text.slice(0, -1).replace(/\r\n?/g, "\n")}\r`;
  } else {
    normalizedText = text.replace(/\r\n?/g, "\n");
  }
  let buffer;
  if (carry.endsWith("\r") && normalizedText.startsWith("\n")) {
    // CRLF split exactly across the chunk boundary: carry ends in a bare
    // \r (left un-normalized last call since we didn't yet know whether
    // a \n would follow), and it just arrived as the first char of text.
    buffer = carry.slice(0, -1) + normalizedText;
  } else {
    buffer = carry + normalizedText;
  }

  const events = [];
  let blockStart = 0;
  let searchFrom = Math.max(0, carry.length - 1);
  let idx;
  // eslint-disable-next-line no-cond-assign
  while ((idx = buffer.indexOf("\n\n", Math.max(searchFrom, blockStart))) !== -1) {
    const block = buffer.slice(blockStart, idx);
    blockStart = idx + 2;
    const evt = block.trim() ? parseSSEBlock(block) : null;
    if (evt) events.push(evt);
  }
  return { events, carry: buffer.slice(blockStart) };
};

export class ResumableEventSource extends EventTarget {
  static CONNECTING = CONNECTING;
  static OPEN = OPEN;
  static CLOSED = CLOSED;

  constructor(url, { lastEventId = "", withCredentials = false, headers = {}, fallbackUrl = null } = {}) {
    super();
    this.url = url;
    this.readyState = CONNECTING;
    this.withCredentials = withCredentials;
    this._extraHeaders = headers;
    // PLEX-6261: where to connect instead when `url` answers 404 (see _connect).
    this._fallbackUrl = fallbackUrl;
    this._lastEventId = lastEventId;
    this._abortController = null;
    this._closed = false;
    // PLEX-6110/WEBAPP-7G instrumentation: rolling log of the last N parsed
    // SSE events (not the full payload — just enough to reconstruct ordering)
    // so a duplicate-message_start error can show exactly what arrived right
    // before it: was a toolResult event actually delivered and dropped
    // downstream, or never delivered at all (backend ordering issue)?
    this._recentEvents = [];

    // EventSource-style on* handlers
    this.onopen = null;
    this.onmessage = null;
    this.onerror = null;

    this._connect();
  }

  get lastEventId() {
    return this._lastEventId;
  }

  async _connect() {
    if (this._closed) return;
    this.readyState = CONNECTING;
    this._abortController = new AbortController();

    const headers = {
      Accept: "text/event-stream",
      "Cache-Control": "no-cache",
      ...this._extraHeaders,
    };
    if (this._lastEventId) {
      headers["Last-Event-ID"] = this._lastEventId;
    }

    let response;
    try {
      response = await fetch(this.url, {
        method: "GET",
        credentials: this.withCredentials ? "include" : "same-origin",
        headers,
        signal: this._abortController.signal,
      });
    } catch (err) {
      if (this._closed) return;
      this._emitError(err);
      this.readyState = CLOSED;
      return;
    }

    if (!response.ok) {
      // 404 = channel doesn't exist OR producer is dead — the backend
      // probe gates this, but a probe-then-SSE race can land here if
      // the heartbeat expired in the ~100ms window between the two
      // requests (or after a Flask restart). Treat as a clean close
      // rather than an error; DynamoDB hydration is the fallback and
      // the user gets no spurious "Server error (HTTP 500/503)"
      // Sentry noise. Other 4xx/5xx still surface as errors.
      if (response.status === 404) {
        // PLEX-6261: a backend deployed before GET /stream/{id}/sse answers that path
        // 404. Connect on the fallback (the old URL) instead, once; a stream that's
        // really gone answers 404 there too, and closes as below.
        if (this._fallbackUrl && this.url !== this._fallbackUrl) {
          this.url = this._fallbackUrl;
          return this._connect();
        }
        this.readyState = CLOSED;
        this._emit("close");
        return;
      }
      this._emitError(new Error(`SSE connect failed: ${response.status}`));
      this.readyState = CLOSED;
      return;
    }

    this.readyState = OPEN;
    this._emit("open");

    const reader = response.body?.getReader();
    if (!reader) {
      this._emitError(new Error("SSE response has no body"));
      this.readyState = CLOSED;
      return;
    }

    const decoder = new TextDecoder("utf-8");
    let carry = "";
    // Chaos hookup — track message-event count + connection start time so
    // the F-scenario presets that ride on `sse.dropAfterEventCount` /
    // `sse.dropAtElapsedMs` / `sse.latencyMs` apply here too.
    let messageCount = 0;
    const connectStartMs = Date.now();
    const chaosFn = _tryLoadChaos();
    const getChaosState = () => {
      if (!chaosFn) return null;
      try {
        const s = chaosFn().getState();
        return s.enabled ? s : null;
      } catch (_) {
        return null;
      }
    };
    try {
      while (!this._closed) {
        const { value, done } = await reader.read();
        if (done) break;
        const text = decoder.decode(value, { stream: true });
        const { events, carry: nextCarry } = parseSSEChunk(text, carry);
        carry = nextCarry;
        for (const evt of events) {
          // Re-check the closed flag BEFORE emitting each event in the
          // batch. A single fetch read() can hand parseSSEChunk a buffer
          // containing many events; without this check, a listener that
          // calls close() on event N still receives events N+1..end of the
          // same batch (the outer while-loop's check doesn't re-run between
          // events). On the chat surface that surfaced as a flood of
          // "[LLMClient] Stale event guard fired: mtype=content_block_delta
          // ... streamId=null active=false" warnings after a normal
          // end_turn completion — the "message" handler nulled streamId
          // and called srcElement.close(), but the remaining content_block_*
          // events in the same buffered batch kept arriving.
          if (this._closed) return;
          // Chaos: latency injection — delay each event delivery.
          const c = getChaosState();
          if (c?.sse?.latencyMs > 0) {
            await sleep(c.sse.latencyMs);
            if (this._closed) return;
          }
          if (evt.lastEventId) this._lastEventId = evt.lastEventId;
          this._recordRecentEvent(evt);
          this._emit(evt.type, evt);
          if (evt.type === "message") messageCount += 1;

          // Chaos: drop trigger — synthesize an error event + close the
          // reader. useSSEListener's autoReconnect (step 11 wiring)
          // catches the error and reissues with Last-Event-ID, so the
          // F5-network-blip preset actually exercises the resume path.
          if (c?.sse) {
            const dropByCount =
              c.sse.dropAfterEventCount != null &&
              messageCount >= c.sse.dropAfterEventCount;
            const dropByElapsed =
              c.sse.dropAtElapsedMs != null &&
              Date.now() - connectStartMs >= c.sse.dropAtElapsedMs;
            if ((dropByCount || dropByElapsed) && !this._chaosDropped) {
              this._chaosDropped = true;
              const dropErr = new Error("ChaosController: simulated SSE drop");
              dropErr.isChaosDrop = true;
              this._emitError(dropErr);
              // Abort the current fetch reader so the read() loop exits.
              // Do NOT set readyState = CLOSED — useSSEListener's auto-
              // reconnect logic skips the reconnect when readyState is
              // CLOSED. Leaving it OPEN lets the consumer's error
              // handler call eventSource.reconnect() and we resume.
              try { this._abortController?.abort(ABORT_REASON_CLOSED); } catch (_) {}
              return;
            }
          }
        }
      }
    } catch (err) {
      // AbortError can only come from our own AbortController.abort() —
      // i.e. close() or reconnect(). Both are intentional teardowns of
      // the current connection and should be silent (no error event).
      //
      // The old condition `_closed && AbortError → return` covered close()
      // but missed reconnect(): during reconnect _closed is still false
      // (we're starting a new connection, not tearing down), so the abort
      // bubbled through _emitError → useSSEListener.js:108 → another
      // auto-reconnect → another abort. The cycle ate MAX_AUTO_RECONNECTS
      // (5) in ~5s and then the engine cancelled the agent. Confirmed
      // live 2026-05-18: FE __sseLog showed 1-second-interval aborts
      // after every coach turn completion, followed by /api/agent/cancel.
      //
      // We can't reliably distinguish "close abort" from "reconnect abort"
      // by signal.reason here because `this._abortController` may already
      // point to the new (reconnect-created) controller by the time this
      // catch runs (queueMicrotask in reconnect() races with the rejection
      // microtask from the old reader.read()). Suppressing every
      // AbortError is correct because fetch never produces AbortError
      // except via abort() — real transport errors surface as TypeError.
      if (err?.name === "AbortError") return;
      this._emitError(err);
    } finally {
      try { reader.releaseLock?.(); } catch (_) { /* ignore */ }
    }
    this.readyState = CLOSED;
  }

  // Cheap by design: no JSON.parse (a 100-event burst, each potentially
  // carrying a large content_block_delta string, must not add O(n) parse
  // cost per event). `preview` is a raw substring, not parsed JSON — good
  // enough to spot type/role/agent_id since Python dict serialization keeps
  // insertion order and those keys land near the front of the payload.
  _recordRecentEvent(evt) {
    this._recentEvents.push({
      type: evt.type,
      lastEventId: evt.lastEventId || null,
      ts: Date.now(),
      dataLen: evt.data?.length ?? 0,
      preview: (evt.data || "").slice(0, 160),
    });
    if (this._recentEvents.length > 30) this._recentEvents.shift();
  }

  getRecentEvents() {
    return this._recentEvents.slice();
  }

  _emit(type, payload = {}) {
    // Build an Event with .data/.lastEventId/.origin so consumers that
    // treat us as a drop-in EventSource don't see undefined.
    const evt = new Event(type);
    evt.data = payload.data || "";
    evt.lastEventId = payload.lastEventId || "";
    evt.origin = (() => {
      try { return new URL(this.url, window.location.origin).origin; } catch (_) { return ""; }
    })();
    // Native EventSource exposes the source via `srcElement` (legacy alias
    // for `target`). LLMClient.handleStreamEvent reads `event.srcElement.close()`
    // on stream completion — without this, the call silently fails because
    // dispatchEvent only sets `target`, leaving srcElement undefined.
    try { Object.defineProperty(evt, "srcElement", { value: this, configurable: true }); } catch (_) { /* ignore */ }

    // onfoo property handler.
    const handler = this[`on${type}`];
    if (typeof handler === "function") {
      try { handler(evt); } catch (_) { /* native swallows */ }
    }
    // addEventListener handlers via EventTarget.
    this.dispatchEvent(evt);
  }

  _emitError(err) {
    const evt = new Event("error");
    evt.error = err;
    evt.message = err?.message || String(err);
    try { Object.defineProperty(evt, "srcElement", { value: this, configurable: true }); } catch (_) {}
    if (typeof this.onerror === "function") {
      try { this.onerror(evt); } catch (_) {}
    }
    this.dispatchEvent(evt);
  }

  /**
   * Reconnect using the current (or explicitly-supplied) Last-Event-ID.
   * Closes the in-flight fetch first so we don't leak readers.
   */
  reconnect({ lastEventId = null } = {}) {
    if (lastEventId != null) this._lastEventId = lastEventId;
    if (this._abortController) {
      try { this._abortController.abort(ABORT_REASON_CLOSED); } catch (_) {}
    }
    // Reset chaos drop budget on reconnect so the same preset doesn't
    // immediately re-fire on the first new event after resume.
    this._chaosDropped = false;
    // Loop the connect on the microtask so the abort settles first.
    queueMicrotask(() => {
      if (this._closed) return;
      this._connect();
    });
  }

  close() {
    this._closed = true;
    if (this._abortController) {
      try { this._abortController.abort(ABORT_REASON_CLOSED); } catch (_) {}
    }
    this.readyState = CLOSED;
  }
}

export default ResumableEventSource;
export { parseSSEChunk };
